import promptPayLogo from '../assets/brand/promptpay-logo.png';

/**
 * A PromptPay QR shown either inside a navy-framed card (badge, QR,
 * payee-name/amount footer — matching the Thai Bankers' Association's own
 * recommended display convention for a static QR) or plain (just the image
 * plus amount/expiry text, no frame or badge).
 *
 * `framed` (default true) is for the LLMR-122 company-QR fallback, which
 * is a bare EMVCo QR we generate ourselves — the frame and badge are what
 * make it look like a legitimate payment method rather than an unbranded
 * image. `framed={false}` is for the Omise-issued QR: that `payment_qr`
 * image already comes fully branded from Omise/the Thai QR Payment
 * template baked into its own pixels, so wrapping it in another frame and
 * another PromptPay badge doubled up visually (reported 2026-09-01) —
 * plain is just the image and, if given, the amount/expiry as small text.
 *
 * downloadQrImage.ts's composeCard draws the matching layout onto a
 * canvas for the saved PNG, so what a customer sees on screen and what
 * they save look the same either way.
 *
 * The badge is the real PromptPay logo (assets/brand/promptpay-logo.png,
 * user-supplied) — an earlier hand-drawn SVG approximation
 * (PaymentBrandMarks.tsx's PromptPayMark, still used elsewhere e.g. the
 * payment-method picker) looked untrustworthy at this size and was
 * rejected. Deliberately still does NOT reproduce the "Thai QR Payment"
 * association's own separate logo/wordmark — that is their institutional
 * branding, not a payment-method mark like this one.
 */
export type QRCardProps = {
  qrSrc: string;
  qrAlt: string;
  amountLabel: string;
  payeeName?: string | null;
  expiresLabel?: string | null;
  framed?: boolean;
};

export function QRCard({ qrSrc, qrAlt, amountLabel, payeeName, expiresLabel, framed = true }: QRCardProps) {
  if (!framed) {
    return (
      <div className="qr-card qr-card--plain">
        <img src={qrSrc} alt={qrAlt} className="qr-card-image" />
        <p className="qr-card-plain-caption">
          {amountLabel}
          {expiresLabel ? ` · ${expiresLabel}` : ''}
        </p>
      </div>
    );
  }
  return (
    <div className="qr-card">
      <img src={promptPayLogo} alt="PromptPay" className="qr-card-badge" />
      <img src={qrSrc} alt={qrAlt} className="qr-card-image" />
      <div className="qr-card-footer">
        <span className="qr-card-payee">{payeeName}</span>
        <span className="qr-card-amount">{amountLabel}</span>
      </div>
      {expiresLabel && <p className="qr-card-expires">{expiresLabel}</p>}
    </div>
  );
}
